import produce from "immer";
import {
  IMPORT_STARTED,
  IMPORT_QUEUED,
  IMPORT_PROJECT_STARTED,
  IMPORT_PROJECT_COMPLETED,
  IMPORT_PROJECT_FAILED,
  IMPORT_ALL_COMPLETED,
  IMPORT_ALL_FAILED,
  IMPORT_DISMISS,
  IMPORT_POPUP_TOGGLE,
  IMPORT_UPLOAD_FAILED,
} from "../types";

const PROJECT_STATUS = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  fileName: null,
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  // ordered list matching the server-side parse order
  projects: [],
};

const initialState = { jobs: {} };

function upsertProject(job, itemIndex, patch) {
  if (itemIndex == null) return;
  const idx = job.projects.findIndex((p) => p.index === itemIndex);
  if (idx === -1) {
    job.projects.push({
      index: itemIndex,
      label: patch.label ?? "",
      status: PROJECT_STATUS.QUEUED,
      weight: 1,
      startedAt: null,
      completedAt: null,
      error: null,
      ...patch,
    });
    return;
  }
  Object.assign(job.projects[idx], patch);
}

function finalizeIfAllDone(job) {
  if (!job.active) return;
  const total = job.totalProjects;
  if (!total) return;
  if (job.completedCount + job.failedCount < total) return;
  job.active = false;
  job.etaSeconds = 0;
  if (!job.terminatedAt) job.terminatedAt = Date.now();
}

export default function importReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case IMPORT_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        // Merge onto an existing job so STARTED can also carry progress updates
        // (rehydration re-dispatches STARTED with fresh counts/eta). On a fresh
        // start prev is undefined → behaviour is the original full init.
        const prev = draft.jobs[jobId];
        draft.jobs[jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          ...payload,
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload?.startedAt ?? Date.now(),
          fileName: payload?.fileName ?? prev?.fileName ?? null,
        };
      });

    case IMPORT_QUEUED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = true;
        job.totalProjects = payload.total ?? (payload.projects?.length ?? 0);
        job.avgSecondsPerWeight = payload.avgSecondsPerWeight ?? null;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        job.projects = (payload.projects || []).map((p) => ({
          index: p.index,
          label: p.label,
          weight: p.weight ?? 1,
          status: PROJECT_STATUS.QUEUED,
          startedAt: null,
          completedAt: null,
          error: null,
        }));
      });

    case IMPORT_PROJECT_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.IN_PROGRESS,
          startedAt: payload.startedAt ?? Date.now(),
        });
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case IMPORT_PROJECT_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.COMPLETED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: null,
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        if (payload.avgSecondsPerWeight != null) {
          job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
        }
        // BE intermittently drops the terminal ALL_COMPLETED frame under STOMP;
        // transition the card to "done" once the last per-project frame lands.
        // IMPORT_ALL_COMPLETED below remains idempotent.
        finalizeIfAllDone(job);
      });

    case IMPORT_PROJECT_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.FAILED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: payload.error || "Unknown error",
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        if (payload.avgSecondsPerWeight != null) {
          job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
        }
        finalizeIfAllDone(job);
      });

    case IMPORT_ALL_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.etaSeconds = 0;
        job.terminatedAt = Date.now();
      });

    case IMPORT_ALL_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Import failed";
        job.terminatedAt = Date.now();
      });

    case IMPORT_UPLOAD_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Upload failed";
        job.terminatedAt = Date.now();
      });

    case IMPORT_POPUP_TOGGLE:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.open = payload?.open ?? !job.open;
      });

    case IMPORT_DISMISS:
      return produce(state, (draft) => {
        const jobId = payload?.jobId ?? payload;
        if (jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) {
              delete draft.jobs[id];
            }
          });
        } else {
          delete draft.jobs[jobId];
        }
      });

    default:
      return state;
  }
}

export { PROJECT_STATUS };
